"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import Button from "@components/atoms/Button";
import styles from "./Carousel.module.scss";
import Text from "@components/atoms/Text";
import Image from "@components/atoms/Image";
import assetLoader from "@utils/internalImageLoder";

interface CarouselProps {
  children: React.ReactNode[];
  className?: string;
  heading?: string;
  headingClassName?: string;
  leftArrowPath?: string;
  rightArrowPath?: string;
  leftArrowPathDark?: string;
  rightArrowPathDark?: string;
  arrowButtonClass?: string;
  carouselWrapperClassName?: string;
  headingWrapperClassName?: string;
  carouselItemClassName?: string;
  isDark?: boolean;
  showMobileDots?: boolean;
}

export const Carousel = ({
  children,
  className = "",
  heading = "",
  headingClassName = "",
  leftArrowPath = `${assetLoader()}/icons/Carousal/ArrowLeft.svg`,
  rightArrowPath = `${assetLoader()}/icons/Carousal/ArrowRight.svg`,
  leftArrowPathDark = `${assetLoader()}/icons/Carousal/ArrowLeftDark.svg`,
  rightArrowPathDark = `${assetLoader()}/icons/Carousal/ArrowRightDark.svg`,
  arrowButtonClass = "",
  carouselWrapperClassName = "",
  headingWrapperClassName = "",
  carouselItemClassName = "",
  isDark = false,
  showMobileDots = false,
}: CarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDesktop, setIsDesktop] = useState(true);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleResize = () => setIsDesktop(mediaQuery.matches);

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleResize);

    return () => mediaQuery.removeEventListener("change", handleResize);
  }, []);


  const updateScrollIndicators = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    if (isDesktop) {
      setIsScrollable(scrollWidth > clientWidth);
      setIsAtStart(Math.round(scrollLeft) <= 5);
      setIsAtEnd(Math.round(scrollLeft + clientWidth) >= Math.round(scrollWidth));
    } else if (showMobileDots) {
      // Calculate current slide index for mobile dots
      const itemWidth = container.scrollWidth / children.length;
      const newIndex = Math.round(scrollLeft / itemWidth);
      setCurrentSlideIndex(Math.min(newIndex, children.length - 1));
    }
  }, [isDesktop, showMobileDots, children.length]);


  const scroll = useCallback((direction: "left" | "right") => {
    if (!isDesktop) return; // skip on mobile
    const container = containerRef.current;
    if (!container) return;

    const scrollAmount = container.offsetWidth * 0.9;
    container.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  }, [isDesktop]);

  const scrollToSlide = useCallback((slideIndex: number) => {
    if (isDesktop || !showMobileDots) return;
    const container = containerRef.current;
    if (!container) return;

    const itemWidth = container.scrollWidth / children.length;
    container.scrollTo({
      left: slideIndex * itemWidth,
      behavior: "smooth",
    });
  }, [isDesktop, showMobileDots, children.length]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    updateScrollIndicators();
    container.addEventListener("scroll", updateScrollIndicators);

    return () => {
      container.removeEventListener("scroll", updateScrollIndicators);
    };
  }, [children.length, isDesktop, showMobileDots]);


  return (
    <div className={`${styles.carouselWrapper} ${carouselWrapperClassName}`}>
      <div className={`${styles.headingWrapper} ${headingWrapperClassName}`}>
        <Text level="h2" className={`${styles.heading} ${headingClassName}`}>
          {heading}
        </Text>
        {isDesktop && isScrollable && (
          <div className={styles.controls}>
            <Button
              onClick={() => scroll("left")}
              className={`${styles.arrowButton} ${arrowButtonClass}`}
              aria-label="Scroll left"
              disabled={isAtStart}
            >
              <Image
                src={isDark ? leftArrowPathDark : leftArrowPath}
                alt={"Left Arrow"}
                className={styles.navIcon}
                width={20}
                height={20}
              />
            </Button>
            <Button
              onClick={() => scroll("right")}
              className={`${styles.arrowButton} ${arrowButtonClass}`}
              aria-label="Scroll right"
              disabled={isAtEnd}
            >
              <Image
                src={isDark ? rightArrowPathDark : rightArrowPath}
                alt={"Right Arrow"}
                className={styles.navIcon}
                width={20}
                height={20}
              />
            </Button>
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        className={`${styles.carouselContainer} ${className} ${
          isDesktop ? styles.snap : styles.mobileScroll
        }`}
      >
        {children.map((child, index) => {
          const childKey = React.isValidElement(child) && child.key 
            ? String(child.key) 
            : `carousel-item-${index}`;
          
          return (
            <div
              key={childKey}
              className={`${styles.carouselItem} ${carouselItemClassName}`}
            >
              <div className={styles.carouselItemInner}>{child}</div>
            </div>
          );
        })}
      </div>

      {/* Mobile Dots Navigation */}
      {!isDesktop && showMobileDots && children.length > 1 && (
        <div className={styles.mobileDots}>
          {children.map((_, index) => (
            <button
              key={`carousel-dot-${index}`}
              className={`${styles.dot} ${
                index === currentSlideIndex ? styles.activeDot : ""
              }`}
              onClick={() => scrollToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
